import { Check, Crown, EyeOff, LogOut, Share2, UserPlus, Users } from 'lucide-react'
import { canInviteFromList } from '../lib/groupList'
import { cn } from '../lib/utils'
import { Badge } from './ui/badge'
import { Button } from './ui/button'

// One row on /groups. Rows are tinted by your relation to the group (client
// 2026-10-12): groups you own get a purple ring and a filled crown tile so
// they stand apart, and each row offers the one action that fits it:
// invite (owner), leave (joined) or join (any other public group).
export default function GroupListRow({ group, busy = false, onOpen, onInvite, onLeave, onJoin }) {
  const memberCount = Number(group.member_count || 0)
  const isOwner = group.relation === 'owner'
  const isJoined = group.relation === 'member'
  const canInvite = canInviteFromList(group)
  const canJoin = group.relation === 'other' && group.is_public !== false

  // The row itself opens the group; the action buttons must not.
  const act = (fn) => (e) => {
    e.stopPropagation()
    fn?.(group)
  }

  return (
    <div
      className={cn(
        'card-glass-item flex flex-wrap sm:flex-nowrap items-center gap-x-4 gap-y-3 p-4 rounded-lg cursor-pointer',
        isOwner && 'ring-2 ring-primary/60 bg-primary/10 dark:bg-primary/20',
      )}
      onClick={() => onOpen?.(group)}
      data-relation={group.relation}
      data-testid="group-row"
    >
      <div
        className={cn(
          'h-12 w-12 rounded-xl flex items-center justify-center flex-shrink-0',
          isOwner ? 'bg-primary text-primary-foreground' : 'bg-primary/10 text-primary',
        )}
      >
        {isOwner ? <Crown className="h-6 w-6" aria-hidden="true" /> : <Users className="h-6 w-6" aria-hidden="true" />}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex flex-wrap items-center gap-2 mb-1">
          <h3 className="font-semibold break-words">{group.name}</h3>
          {isOwner && <Badge variant="solid-purple">Owner</Badge>}
          {isJoined && (
            <Badge variant="secondary" className="gap-1">
              <Check className="h-3 w-3" />
              Joined
            </Badge>
          )}
          {group.mine && group.private && (
            <Badge variant="outline" className="gap-1 text-muted-foreground">
              <EyeOff className="h-3 w-3" />
              Hidden
            </Badge>
          )}
        </div>
        {/* Owner name sits right before the count (client 2026-10-19). */}
        <div className="text-sm text-muted-foreground break-words">
          {group.owner_name && (
            <><span className="font-semibold text-foreground">Owner: {group.owner_name}</span> · </>
          )}
          {memberCount} {memberCount === 1 ? 'member' : 'members'}
        </div>
        {group.description && (
          <p className="text-sm text-muted-foreground mt-1 break-words">{group.description}</p>
        )}
      </div>
      {/* The invite icon sits beside the name. On a phone the wider
          Leave/Join button drops below the text, lined up with it. */}
      {(canInvite || isJoined || canJoin) && (
      <div className={cn('flex-shrink-0', !canInvite && 'w-full pl-16 sm:w-auto sm:pl-0')}>
        {canInvite && (
          <Button
            variant="outline"
            size="icon"
            onClick={act(onInvite)}
            aria-label={`Invite people to ${group.name}`}
            title="Invite people"
          >
            <Share2 className="h-4 w-4" />
          </Button>
        )}
        {isJoined && (
          <Button variant="outline" size="sm" onClick={act(onLeave)} disabled={busy}>
            <LogOut className="h-4 w-4 mr-2" />
            {busy ? 'Leaving...' : 'Leave Group'}
          </Button>
        )}
        {canJoin && (
          <Button variant="primaryNeon" size="sm" onClick={act(onJoin)} disabled={busy}>
            <UserPlus className="h-4 w-4 mr-2" />
            {busy ? 'Joining...' : 'Join Group'}
          </Button>
        )}
      </div>
      )}
    </div>
  )
}
